import { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  MoreHorizontal,
  MapPin,
  Phone,
  Star,
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Printer,
  Building2,
} from "lucide-react";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { ImportDialog } from "@/components/shared/ImportDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useRowSelection } from "@/hooks/useRowSelection";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { ConfigLabel } from "@/components/shared/ConfigLabel";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { SortableHeader } from "@/components/shared/SortableHeader";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useImportRefs } from "@/hooks/useImportRefs";
import { findByCode, runUpsertImport } from "@/lib/import-kit";
import { emsTabClass } from "@/components/ui/ems-tabs";

/** Cột Excel NCC — dùng chung cho xuất, file mẫu và nhập (04/11/2026); Nợ hiện tại chỉ xuất (sổ công nợ tính) */
const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "code", label: "Mã NCC", required: true },
  { key: "shortName", label: "Tên ngắn" },
  { key: "name", label: "Tên nhà cung cấp", required: true },
  { key: "taxCode", label: "Mã số thuế" },
  { key: "representative", label: "Người đại diện" },
  { key: "contactPerson", label: "Người liên hệ" },
  { key: "phone", label: "SĐT" },
  { key: "email", label: "Email" },
  { key: "address", label: "Địa chỉ" },
  { key: "group", label: "Nhóm NCC", ref: "vendorGroup" },
  { key: "type", label: "Loại hình", ref: "vendorType" },
  { key: "bankName", label: "Ngân hàng" },
  { key: "bankAccountNumber", label: "Số TK" },
  { key: "bankAccountHolder", label: "Chủ TK" },
  { key: "initialDebt", label: "Nợ đầu kỳ", type: "number", min: 0 },
  { key: "remainingDebt", label: "Nợ hiện tại", type: "number", importable: false },
  { key: "status", label: "Trạng thái", type: "enum", enumValues: ["ACTIVE", "INACTIVE"] },
];

const FILTER_FIELDS: FilterField[] = [
  { key: "code", label: "Mã NCC", type: "text", placeholder: "NCC-..." },
  { key: "name", label: "Tên NCC", type: "text", placeholder: "Nhập tên..." },
  {
    key: "contactPerson",
    label: "Liên hệ",
    type: "text",
    placeholder: "Người liên hệ...",
  },
  { key: "phone", label: "Điện thoại", type: "text", placeholder: "08x..." },
  {
    key: "address",
    label: "Địa chỉ",
    type: "text",
    placeholder: "Nhập địa chỉ...",
  },
  { key: "group", label: "Nhóm NCC", type: "text", placeholder: "Nhóm..." },
  {
    key: "status",
    label: "Trạng thái",
    type: "combobox-multi",
    options: [
      { value: "ACTIVE", label: "Hoạt động" },
      { value: "INACTIVE", label: "Ngưng" },
    ],
  },
];

export default function VendorsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  const {
    paginatedItems,
    isLoading,
    error,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    filtered,
    groupFilter,
    onGroupChange,
    groupCounts,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "vendors",
    queryFn: (params) => trpcClient.vendors.list.query(params) as any,
    defaultLimit: 500,
    groupParamName: "group",
    groupCountsQueryFn: () => trpcClient.vendors.groupCounts.query() as any,
    extraParams: {
      isDeleted: showDeleted || undefined,
    },
  });

  const { options: vendorGroupOptions } = useConfigLookup("vendor_groups");

  const filterFields = useMemo<FilterField[]>(() => {
    return FILTER_FIELDS.map((f) => {
      if (f.key === "group") {
        return {
          key: "group",
          label: "Nhóm NCC",
          type: "combobox-multi",
          options:
            vendorGroupOptions.length > 0
              ? vendorGroupOptions.map((g) => ({ value: g.id, label: g.label }))
              : [
                  { value: "XUONG_IN", label: "Xưởng in" },
                  { value: "GIA_CONG", label: "Gia công sau in" },
                  { value: "VAT_TU", label: "Cung cấp vật tư" },
                  { value: "KHUON_MAU", label: "Khuôn mẫu & Dao bế" },
                ],
        };
      }
      return f;
    });
  }, [vendorGroupOptions]);

  const rowIds = paginatedItems.map((v: any) => v.id || v.code);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectionMode, clearSelection, selectedCount } =
    rowSelection;

  // Sidebar groups (from separate lightweight API — stable regardless of filter)
  const sidebarItems = useMemo(() => {
    return (groupCounts as any[])
      .map((c: any) => ({
        id: c.id,
        label:
          (c.label || "Chưa phân nhóm").length > 18
            ? (c.label || "Chưa phân nhóm").slice(0, 18) + "…"
            : c.label || "Chưa phân nhóm",
        count: c.count,
      }))
      .sort((a: any, b: any) =>
        a.id === "_none"
          ? 1
          : b.id === "_none"
            ? -1
            : a.label.localeCompare(b.label),
      );
  }, [groupCounts]);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_nha_cung_cap",
    sheetName: "Nhà Cung Cấp",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.vendors.list.query(params),
    currentFilters: serverParams,
    selectedIds,
    idKey: "id",
  });

  // Nhập Excel NCC (04/11/2026): mã đã có → cập nhật, mã mới → tạo; Nhóm / Loại hình khớp danh mục Cấu hình
  const importRefs = useImportRefs();
  const vendorImportRefs = useMemo(
    () => ({ vendorGroup: importRefs.vendorGroup, vendorType: importRefs.vendorType }),
    [importRefs],
  );
  const handleImport = async (rows: Record<string, unknown>[]) => {
    const result = await runUpsertImport(rows, {
      codeOf: (row) => String(row.code ?? "").trim(),
      findExisting: findByCode(() => trpcClient.vendors.list.query({ limit: 10000 } as any), (v) => v.code),
      prepare: (row, mode) => {
        const { remainingDebt: _debt, code, ...rest } = row as any;
        return mode === "create" ? { ...rest, code } : rest;
      },
      create: (data) => trpcClient.vendors.create.mutate(data as any),
      update: (id, data) => trpcClient.vendors.update.mutate({ id, data } as any),
    });
    await queryClient.invalidateQueries({ queryKey: ["vendors"] });
    return result;
  };

  const handleStatusChangeAction = async (id: string, newStatus: string) => {
    try {
      await trpcClient.vendors.update.mutate({
        id,
        data: { status: newStatus } as any,
      });

      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors-lookup"] });
    } catch (err: any) {
      toast.error(err?.message || "Cập nhật trạng thái thất bại");
    }
  };

  const getBulkActions = (): BulkAction[] => {
    const sel = paginatedItems.filter((v: any) => selectedIds.has(v.id || v.code));
    if (!sel.length) return [];
    const actions: BulkAction[] = [
      {
        label: "Xuất Excel",
        icon: <Printer className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
    ];
    if (hasPermission("vendors", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: sel.map((v: any) => v.id || v.code),
            actionFn: async (id) => trpcClient.vendors.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "nhà cung cấp",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["vendors"] });
              queryClient.invalidateQueries({ queryKey: ["vendors-lookup"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} nhà cung cấp đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  const handleDelete = async (id: string) => {
    try {
      await trpcClient.vendors.delete.mutate({ id });
      toast.success("Đã xóa bản ghi thành công.");

      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors-lookup"] });
    } catch (err: any) {
      toast.error(err?.message || "Xoá Nhà cung cấp thất bại");
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await trpcClient.vendors.restore.mutate({ id });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors-lookup"] });
      toast.success("Đã phục hồi bản ghi thành công.");
    } catch (err: any) {
      toast.error(err?.message || "Phục hồi Nhà cung cấp thất bại.");
    }
  };

  if (error)
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 font-medium">Lỗi khi tải dữ liệu</p>
        <Button className="mt-4" onClick={() => window.location.reload()}>
          Thử lại
        </Button>
      </div>
    );

  const COLS = 8;

  return (
    <>
      <ListPageShell
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowDeleted(false)}
              className={emsTabClass(!showDeleted, { size: "sm" })}
            >
              <Building2 className="w-3.5 h-3.5 text-indigo-600" />
              <span>Đang Hợp Tác</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  !showDeleted
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {!showDeleted ? totalItems : (groupCounts?.reduce((s: number, c: any) => s + (c.count || 0), 0) || totalItems)}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setShowDeleted(true)}
              className={emsTabClass(showDeleted, { size: "sm", tone: "rose" })}
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              <span>Thùng rác</span>
            </button>
          </div>
        }
        headerFilters={
          <div className="flex items-center gap-2 flex-wrap">
            <PageFilterTags
              items={sidebarItems.map((item) => ({
                key: item.id,
                label: item.label,
                count: item.count,
                color: item.id === "_ungrouped" ? ("slate" as const) : ("blue" as const),
              }))}
              selectedKeys={groupFilter ? [groupFilter] : []}
              onSelect={(key) =>
                onGroupChange(key === groupFilter ? null : key)
              }
              dropdownLabel="Nhóm NCC"
            />
          </div>
        }
        unitLabel="NCC"
        actions={
          <ExportImportButtons
            onExport={handleExport}
            exportLoading={isExporting}
            onImportClick={
              hasPermission("vendors", "create")
                ? () => setShowImport(true)
                : undefined
            }
            onDownloadTemplate={() =>
              downloadTemplate(EXPORT_COLUMNS, "nha_cung_cap")
            }
          >
            {hasPermission("vendors", "create") && (
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => navigate({ to: "/vendors/create" })}
              >
                <Plus size={14} className="mr-1" /> Thêm NCC
              </Button>
            )}
          </ExportImportButtons>
        }
        filterFields={filterFields}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <table
          className={cn(
            "w-full text-xs caption-bottom apv-compact-table min-w-[1400px]",
            selectionMode && "apv-select-mode",
          )}
        >
          <thead className="apv-table-header">
            <tr>
              <DataTableSelectAllCheckbox rowSelection={rowSelection} />
              <SortableHeader
                label="Mã NCC"
                field="code"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code w-[90px] whitespace-nowrap"
              />
              <SortableHeader
                label="Nhà Cung Cấp"
                field="name"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[240px] whitespace-nowrap"
              />
              <SortableHeader
                label="Loại hình"
                field="type"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[90px] whitespace-nowrap"
              />
              <SortableHeader
                label="Người liên hệ"
                field="contactPerson"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[160px] whitespace-nowrap"
              />
              <SortableHeader
                label="Đánh giá"
                field="rating"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[100px] whitespace-nowrap"
              />
              <SortableHeader
                label="Nhóm"
                field="group"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[70px] whitespace-nowrap"
              />
              <SortableHeader
                label="Trạng thái"
                field="status"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[75px] whitespace-nowrap"
              />
              <th className="text-right w-[70px] px-2 py-1.5 apv-col-actions whitespace-nowrap">TT</th>
            </tr>
            {showFilter && (
              <InlineFilterRow
                columns={[
                  CHECKBOX_COL_NULL, // checkbox
                  { key: "code" },
                  { key: "name" },
                  null, // loại hình
                  { key: "contactPerson" },
                  null, // đánh giá
                  { key: "group" },
                  { key: "status" },
                  null, // TT
                ]}
                filterFields={FILTER_FIELDS}
                filterValues={filterValues}
                onFilterChange={onFilterChange}
              />
            )}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: COLS }).map((_, j) => (
                    <td key={j} className="px-2 py-2">
                      <Skeleton className="h-3 w-14" />
                    </td>
                  ))}
                </tr>
              ))
            ) : paginatedItems.length === 0 ? (
              <tr>
                <td
                  colSpan={COLS}
                  className="text-center text-slate-400 py-12 text-sm"
                >
                  {searchQuery
                    ? "Không tìm thấy NCC phù hợp"
                    : "Chưa có dữ liệu"}
                </td>
              </tr>
            ) : (
              paginatedItems.map((vendor: any) => (
                <DataTableRow
                  key={vendor.id || vendor.code}
                  id={vendor.id || vendor.code}
                  rowSelection={rowSelection}
                  className="hover:bg-blue-50/40 transition-colors border-b border-slate-100 last:border-0"
                  onClick={() =>
                    navigate({
                      to: "/vendors/$vendorId",
                      params: { vendorId: vendor.code },
                    })
                  }
                >
                  <DataTableRowCheckbox
                    id={vendor.id || vendor.code}
                    rowSelection={rowSelection}
                  />
                  <td className="apv-col-code px-2 py-1.5">
                    <span className="font-mono font-bold text-indigo-600">
                      {vendor.code}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 min-w-[200px] break-words whitespace-normal leading-tight text-[12px]">
                    <div className="font-medium text-slate-900">
                      {vendor.shortName || vendor.name}
                    </div>
                    {vendor.shortName && vendor.shortName !== vendor.name && (
                      <div className="text-[10px] text-slate-400">
                        {vendor.name}
                      </div>
                    )}
                    {vendor.address && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <MapPin className="h-2.5 w-2.5 shrink-0" />
                        <span>{vendor.address}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <ConfigLabel
                      categoryCode="vendor_type"
                      value={vendor.type}
                      fallback="—"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6 border border-slate-200">
                        <AvatarImage
                          src={`https://api.dicebear.com/7.x/initials/svg?seed=${vendor.contactPerson}`}
                        />
                        <AvatarFallback className="text-[9px] font-bold">
                          {vendor.contactPerson?.substring(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-xs font-bold text-slate-700">
                          {vendor.contactPerson || "—"}
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono flex items-center gap-0.5">
                          <Phone className="h-2 w-2" />{" "}
                          {vendor.phone || vendor.mobile || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                      <span className="font-bold text-slate-700">
                        {(vendor as any).rating || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <ConfigLabel
                      categoryCode="vendor_group"
                      value={(vendor as any).group}
                      fallback="—"
                    />
                  </td>
                  <td className="px-2 py-1.5 align-middle">
                    {(vendor.status || "ACTIVE") === "ACTIVE" ? (
                      <Badge
                        variant="outline"
                        className="border-indigo-200 text-indigo-700 bg-indigo-50 font-medium px-1.5 py-0 text-[10px]"
                      >
                        Hoạt động
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-slate-200 text-slate-500 bg-slate-50 font-medium px-1.5 py-0 text-[10px]"
                      >
                        Ngưng
                      </Badge>
                    )}
                  </td>
                  <td
                    className="px-2 py-1.5 text-right w-[70px] apv-col-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <MoreHorizontal className="h-4 w-4 text-slate-400" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[180px]">
                        <DropdownMenuLabel className="text-xs">
                          Thao tác
                        </DropdownMenuLabel>
                        {vendor.isDeleted ? (
                          <>
                            {hasPermission("vendors", "delete") && (
                              <DropdownMenuItem
                                onClick={() => handleRestore(vendor.id)}
                                className="text-emerald-600"
                              >
                                Phục hồi
                              </DropdownMenuItem>
                            )}
                          </>
                        ) : (
                          <>
                            {hasPermission("vendors", "update") && (
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({
                                    to: "/vendors/$vendorId/edit",
                                    params: { vendorId: vendor.code },
                                  })
                                }
                              >
                                <Pencil className="mr-2 h-4 w-4" /> Chỉnh sửa
                              </DropdownMenuItem>
                            )}
                            {hasPermission("vendors", "update") &&
                              ((vendor.status || "ACTIVE") === "ACTIVE" ? (
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleStatusChangeAction(vendor.id, "INACTIVE")
                                  }
                                >
                                  <AlertCircle className="mr-2 h-4 w-4" /> Ngừng hợp tác
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleStatusChangeAction(vendor.id, "ACTIVE")
                                  }
                                >
                                  <AlertCircle className="mr-2 h-4 w-4" /> Hợp tác lại
                                </DropdownMenuItem>
                              ))}
                            {hasPermission("vendors", "delete") && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                                  onClick={async () => {
                                    if (
                                      await confirm(
                                        `Bạn có chắc muốn xoá ${vendor.name}? (Có thể phục hồi trong Thùng rác)`,
                                      )
                                    ) {
                                      handleDelete(vendor.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" /> Xóa
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </DataTableRow>
              ))
            )}
          </tbody>
        </table>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        title="Import Nhà Cung Cấp"
        columns={EXPORT_COLUMNS}
        onImport={handleImport}
        refs={vendorImportRefs}
        onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "nha_cung_cap")}
      />
    </>
  );
}
